/* iter55z-f — Connected Accounts panel (BYO Canva + Adobe).
 *
 * Lets a signed-in The Loom link their personal Canva Connect and
 * Adobe Creative Cloud accounts to the app. Refresh tokens are stored
 * encrypted server-side; this UI only shows connection status +
 * account label and provides Connect / Disconnect actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Link as LinkIcon, Unlink, Palette, Layers, ExternalLink, Activity, CheckCircle2, XCircle } from 'lucide-react';
import { CanvaPulseCard } from '@/components/community/CanvaPulseCard';
import ClaritySettingsCard from './ClaritySettingsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARDS = [
  {
    key:   'canva',
    title: 'Canva Connect',
    icon:  Palette,
    color: 'text-sky-600',
    accent: 'bg-sky-50 border-sky-200',
    blurb: 'Design & export book covers using your personal Canva account. Templates, exports, and brand kits all live in YOUR Canva — we just orchestrate the calls.',
    scopes: 'design read/write, assets, brand templates, profile',
  },
  {
    key:   'adobe',
    title: 'Adobe Creative Cloud',
    icon:  Layers,
    color: 'text-red-600',
    accent: 'bg-red-50 border-red-200',
    blurb: 'Sync cover-art components to your CC Libraries and open finished files directly in Photoshop or InDesign on your desktop.',
    scopes: 'CC Libraries, profile · PDF Services uses an app-level credential (does not charge your quota).',
  },
];

export default function ConnectionsPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState({ canva: [], adobe: [] });
  const [expanded, setExpanded] = useState({ canva: false, adobe: false });
  const [pulseOptIn, setPulseOptIn] = useState(null);   // null = loading, then bool

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/connections/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Sign in to manage connected accounts.');
      } else {
        toast.error('Failed to load connection status.');
      }
      setStatus({ canva: { connected: false }, adobe: { connected: false } });
    }
  }, []);

  const fetchActivity = useCallback(async (provider) => {
    try {
      const r = await axios.get(`${API}/connections/activity`, {
        params: { provider, limit: 10 },
        withCredentials: true,
      });
      setActivity((a) => ({ ...a, [provider]: r.data.events || [] }));
    } catch {
      // Non-fatal
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // iter55z-i — Load current pulse opt-in consent. Failures are silent
  // so a missing endpoint (older backend) simply hides the checkbox.
  useEffect(() => {
    axios.get(`${API}/community/pulse/opt-in`, { withCredentials: true })
      .then((r) => setPulseOptIn(!!r.data?.opt_in))
      .catch(() => setPulseOptIn(false));
  }, []);

  const togglePulseOptIn = async () => {
    const next = !pulseOptIn;
    setPulseOptIn(next);
    try {
      await axios.post(`${API}/community/pulse/opt-in`,
                        { opt_in: next }, { withCredentials: true });
      toast.success(next
        ? 'You may now appear in the "who linked Canva" ticker.'
        : 'Your name will no longer appear in the community ticker.');
    } catch {
      setPulseOptIn(!next);   // roll back on failure
      toast.error('Could not update your preference. Try again.');
    }
  };

  // If OAuth callback bounced back to this page with ?connected=xxx or
  // ?err=yyy, surface a toast and re-fetch.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const c = q.get('connected');
    const e = q.get('err');
    if (c) {
      toast.success(`${c.charAt(0).toUpperCase()}${c.slice(1)} account connected.`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchStatus();
    } else if (e) {
      toast.error(`Connection cancelled: ${e}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus]);

  const startConnect = (provider) => {
    // Full-page redirect so the browser follows the OAuth 302 chain.
    window.location.href = `${API}/${provider}/oauth/start`;
  };

  const disconnect = async (provider) => {
    setBusy(true);
    try {
      await axios.post(`${API}/${provider}/oauth/disconnect`, {}, { withCredentials: true });
      toast.success(`${provider} disconnected.`);
      await fetchStatus();
    } catch {
      toast.error(`Could not disconnect ${provider}.`);
    } finally {
      setBusy(false);
    }
  };

  if (status == null) {
    return (
      <div className="flex items-center gap-2 py-6 text-[12.5px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading connections…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="connections-panel">
      <div>
        <h3 className="text-[14px] font-semibold text-foreground">Connected Accounts</h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Link your own Canva and Adobe Creative Cloud accounts. Nothing is charged to us — you use your own quota, your own designs, your own libraries. Refresh tokens are stored encrypted at rest and you can disconnect any time.
        </p>
      </div>

      {CARDS.map((c) => {
        const s = status[c.key] || { connected: false };
        const Icon = c.icon;
        return (
          <div
            key={c.key}
            className={`rounded-md border p-3 ${s.connected ? 'border-emerald-200 bg-emerald-50/50' : c.accent}`}
            data-testid={`connections-card-${c.key}`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${c.color}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-[13px] font-semibold text-foreground">{c.title}</h4>
                  {s.connected && (
                    <Badge
                      variant="outline"
                      className="border-emerald-400 bg-emerald-100 text-[10px] text-emerald-900"
                      data-testid={`connections-badge-${c.key}`}
                    >
                      Connected
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                  {c.blurb}
                </p>
                <p className="mt-1 text-[10.5px] text-muted-foreground/80">
                  Scopes requested: {c.scopes}
                </p>
                {s.connected && s.account_label && (
                  <p className="mt-1 text-[11px] font-medium text-emerald-900" data-testid={`connections-label-${c.key}`}>
                    Signed in as {s.account_label}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {s.connected ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => disconnect(c.key)}
                    className="h-7 gap-1 text-[11px]"
                    data-testid={`connections-disconnect-${c.key}`}
                  >
                    <Unlink className="h-3 w-3" />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => startConnect(c.key)}
                    className="h-7 gap-1 text-[11px]"
                    data-testid={`connections-connect-${c.key}`}
                  >
                    <LinkIcon className="h-3 w-3" />
                    Connect
                  </Button>
                )}
                {s.connected && c.key === 'adobe' && (
                  <a
                    href="https://assets.adobe.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10.5px] text-red-800/80 hover:text-red-900"
                    data-testid="connections-adobe-portal-link"
                  >
                    Open CC assets <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            </div>
            {/* Recent activity toggle (iter55z-g) */}
            {s.connected && (
              <div className="mt-2 border-t border-emerald-200/70 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    const isOpen = !expanded[c.key];
                    setExpanded((e) => ({ ...e, [c.key]: isOpen }));
                    if (isOpen) fetchActivity(c.key);
                  }}
                  className="inline-flex items-center gap-1 text-[10.5px] font-medium text-foreground/80 hover:text-foreground"
                  data-testid={`connections-activity-toggle-${c.key}`}
                >
                  <Activity className="h-3 w-3" />
                  {expanded[c.key] ? 'Hide recent activity' : 'Show recent activity'}
                </button>
                {expanded[c.key] && (
                  <ul
                    className="mt-1.5 space-y-1 text-[10.5px]"
                    data-testid={`connections-activity-list-${c.key}`}
                  >
                    {(activity[c.key] || []).length === 0 && (
                      <li className="italic text-muted-foreground">No API calls yet.</li>
                    )}
                    {(activity[c.key] || []).map((ev) => (
                      <li
                        key={ev.id}
                        className="flex items-center gap-1.5 text-muted-foreground"
                        data-testid="connections-activity-row"
                      >
                        {ev.status === 'ok'
                          ? <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
                          : <XCircle className="h-2.5 w-2.5 text-red-600" />}
                        <span className="font-mono-stat text-[10px] text-foreground/80">{ev.action}</span>
                        <span className="ml-auto tabular-nums text-[10px]">{new Date(ev.ts).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* iter55z-i — Community pulse (compact) + opt-in consent */}
      <CanvaPulseCard
        variant="compact"
        className=""
        onConnectClick={!status?.canva?.connected ? () => startConnect('canva') : undefined}
        hidden={!!status?.canva?.connected}
        testIdPrefix="connections-canva-pulse"
      />
      <label
        className="flex cursor-pointer items-start gap-2 rounded-sm border border-border/70 bg-card/60 px-3 py-2 text-[11px] text-muted-foreground hover:bg-card"
        data-testid="connections-pulse-opt-in"
      >
        <input
          type="checkbox"
          className="mt-0.5 h-3.5 w-3.5"
          checked={!!pulseOptIn}
          onChange={togglePulseOptIn}
          data-testid="connections-pulse-opt-in-checkbox"
        />
        <span>
          <b className="text-foreground">Include me in the community ticker.</b>{' '}
          When enabled, other authors may see your first name + last initial
          (e.g. &ldquo;Emily K.&rdquo;) in the &ldquo;who just linked Canva&rdquo; widget. This never
          exposes your email or full name — and only shows up when the admin
          has selected the &ldquo;named&rdquo; pulse mode. You can turn it off any time.
        </span>
      </label>

      <div className="rounded-sm bg-muted/30 px-3 py-2 text-[10.5px] text-muted-foreground">
        <b>Privacy note.</b> Your OAuth refresh tokens are encrypted with a symmetric key that lives only on our server. We never see, log, or store your Canva or Adobe passwords. You can revoke access from your Canva/Adobe account portal at any time — that supersedes anything you do here.
      </div>

      {/* ── Analytics: Microsoft Clarity (per-author) ── */}
      <div>
        <h3 className="mb-2 text-[13px] font-semibold text-foreground">Analytics</h3>
        <ClaritySettingsCard />
      </div>
    </div>
  );
}
